import { useState, type FormEvent } from 'react';
import { WHEEL_SPIN_STATUSES, WHEEL_TIERS, type WheelSpinStatus, type WheelSpinsFilters } from '@/api/types';
import { todayUtc } from '@/lib/format';
import { Field, INPUT_CLASS, SELECT_CLASS } from '@/ui/Field';
import { ToggleChip } from '@/ui/ToggleChip';
import { STATUS_HELP, STATUS_LABEL } from './spin-status';
import { DAY_PRESETS, NEEDS_ATTENTION, sameStatuses, type FilterOptions, type Option } from './wheel-filters';

// A value the link carries but the ledger has no spin for (yet) still has to show as chosen.
const withChosen = (options: Option[], chosen: string): Option[] =>
  !chosen || options.some((option) => option.value === chosen)
    ? options
    : [{ value: chosen, label: chosen }, ...options];

function OptionSelect({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: Option[];
  onChange(value: string): void;
}) {
  const shown = withChosen(options, value);
  return (
    <Field id={id} label={label}>
      <select
        id={id}
        className={SELECT_CLASS}
        value={value}
        // A long prize label is cut in the closed select; the tooltip carries all of it.
        title={shown.find((option) => option.value === value)?.label}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Any</option>
        {shown.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

// Typing does not refetch on every key: the player id applies on Enter or when the field loses focus.
function PlayerField({ value, onApply }: { value: string; onApply(value: string): void }) {
  const [draft, setDraft] = useState(value);
  const commit = () => {
    if (draft.trim() !== value) onApply(draft.trim());
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    commit();
  };
  return (
    <form onSubmit={submit} role="search">
      <Field id="wheel-player" label="Player ID" hint="Any part of the PLAYER-… id; press Enter to apply.">
        <input
          id="wheel-player"
          className={`${INPUT_CLASS} font-mono`}
          value={draft}
          placeholder="Everyone"
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
        />
      </Field>
    </form>
  );
}

interface WheelFiltersProps {
  filters: WheelSpinsFilters;
  options: FilterOptions;
  onChange(filters: WheelSpinsFilters): void;
}

export function WheelFilters({ filters, options, onChange }: WheelFiltersProps) {
  const today = todayUtc();
  const set = (changes: Partial<WheelSpinsFilters>) => onChange({ ...filters, ...changes });

  const toggleStatus = (status: WheelSpinStatus) =>
    set({
      statuses: filters.statuses.includes(status)
        ? filters.statuses.filter((chosen) => chosen !== status)
        : [...filters.statuses, status],
    });

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-1.5 text-sm font-semibold">Spin day (UTC)</p>
        <div className="flex flex-wrap items-center gap-2">
          {DAY_PRESETS.map((preset) => {
            const range = preset.range(today);
            return (
              <ToggleChip
                key={preset.label}
                pressed={filters.from === range.from && filters.to === range.to}
                onClick={() => set(range)}
              >
                {preset.label}
              </ToggleChip>
            );
          })}
          <span className="mx-1 text-sm text-slate-400">or</span>
          <div className="w-40">
            <label htmlFor="wheel-from" className="sr-only">
              From (UTC day)
            </label>
            <input
              id="wheel-from"
              type="date"
              className={INPUT_CLASS}
              value={filters.from}
              max={filters.to || today}
              onChange={(event) => set({ from: event.target.value })}
            />
          </div>
          <span className="text-sm text-slate-500">to</span>
          <div className="w-40">
            <label htmlFor="wheel-to" className="sr-only">
              To (UTC day)
            </label>
            <input
              id="wheel-to"
              type="date"
              className={INPUT_CLASS}
              value={filters.to}
              min={filters.from || undefined}
              max={today}
              onChange={(event) => set({ to: event.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="md:col-span-2">
          <PlayerField
            key={filters.playerUuid}
            value={filters.playerUuid}
            onApply={(playerUuid) => set({ playerUuid })}
          />
        </div>
        <Field id="wheel-tier" label="Tier">
          <select
            id="wheel-tier"
            className={SELECT_CLASS}
            value={filters.tier}
            onChange={(event) => set({ tier: event.target.value as WheelSpinsFilters['tier'] })}
          >
            <option value="">Any</option>
            {WHEEL_TIERS.map((tier) => (
              <option key={tier} value={tier}>
                {tier}
              </option>
            ))}
          </select>
        </Field>
        <OptionSelect
          id="wheel-currency"
          label="Deposit currency"
          value={filters.currency}
          options={options.currencies}
          onChange={(currency) => set({ currency })}
        />
        {/* Wheel uids and prize labels are long, so these two get half the row each. */}
        <div className="md:col-span-2">
          <OptionSelect
            id="wheel-wheel"
            label="Wheel"
            value={filters.wheelUid}
            options={options.wheels}
            // Prize options follow the wheel, so a prize picked under another wheel is dropped.
            onChange={(wheelUid) => set({ wheelUid, segmentUid: '' })}
          />
        </div>
        <div className="md:col-span-2">
          <OptionSelect
            id="wheel-prize"
            label="Prize"
            value={filters.segmentUid}
            options={options.prizes}
            onChange={(segmentUid) => set({ segmentUid })}
          />
        </div>
      </div>

      <div>
        <p className="mb-1.5 text-sm font-semibold">Status</p>
        <div className="flex flex-wrap items-center gap-2">
          <ToggleChip pressed={filters.statuses.length === 0} onClick={() => set({ statuses: [] })}>
            Any
          </ToggleChip>
          {WHEEL_SPIN_STATUSES.map((status) => (
            <ToggleChip
              key={status}
              pressed={filters.statuses.includes(status)}
              onClick={() => toggleStatus(status)}
              title={STATUS_HELP[status]}
            >
              {STATUS_LABEL[status]}
            </ToggleChip>
          ))}
          <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
          <ToggleChip
            pressed={sameStatuses(filters.statuses, NEEDS_ATTENTION)}
            onClick={() => set({ statuses: [...NEEDS_ATTENTION] })}
            title={`${STATUS_LABEL.failed} or ${STATUS_LABEL.drawn.toLowerCase()}`}
          >
            Needs attention
          </ToggleChip>
        </div>
      </div>
    </div>
  );
}
